'use client';
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

// Backend integration: GET /api/reports/job-completion?range=7d
const dailyJobs = [
  { date: 'Mar 22', completed: 11, scheduled: 13, delayed: 2 },
  { date: 'Mar 23', completed: 9, scheduled: 10, delayed: 1 },
  { date: 'Mar 24', completed: 14, scheduled: 15, delayed: 3 },
  { date: 'Mar 25', completed: 12, scheduled: 12, delayed: 0 },
  { date: 'Mar 26', completed: 10, scheduled: 14, delayed: 4 },
  { date: 'Mar 27', completed: 13, scheduled: 14, delayed: 2 },
  { date: 'Mar 28', completed: 9, scheduled: 14, delayed: 2 },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2.5 shadow-xl">
      <p className="text-xs text-zinc-400 mb-1.5">{label}</p>
      {payload.map((p: any) => (
        <p key={`jc-tt-${p.dataKey}`} className="text-xs" style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

export default function JobCompletionReport({ dateRange }: { dateRange: string }) {
  const totalScheduled = dailyJobs.reduce((s, d) => s + d.scheduled, 0);
  const totalCompleted = dailyJobs.reduce((s, d) => s + d.completed, 0);
  const totalDelayed = dailyJobs.reduce((s, d) => s + d.delayed, 0);
  const completionRate = Math.round((totalCompleted / totalScheduled) * 100);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Scheduled', value: totalScheduled, color: 'text-zinc-200' },
          { label: 'Completed', value: totalCompleted, color: 'text-emerald-400' },
          { label: 'Delayed', value: totalDelayed, color: 'text-red-400' },
          { label: 'Completion Rate', value: `${completionRate}%`, color: completionRate >= 90 ? 'text-emerald-400' : 'text-amber-400' },
        ].map((s) => (
          <div key={`jc-sum-${s.label}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">{s.label}</p>
            <p className={`text-3xl font-bold tabular-nums ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-zinc-200 mb-4">Daily Job Completion vs Scheduled — {dateRange}</h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={dailyJobs} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(39 39 42)" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'rgb(113 113 122)' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: 'rgb(113 113 122)' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <Legend wrapperStyle={{ fontSize: '11px', color: 'rgb(113 113 122)' }} />
            <Bar dataKey="scheduled" name="Scheduled" fill="#3F3F46" radius={[4, 4, 0, 0]} />
            <Bar dataKey="completed" name="Completed" fill="#34D399" radius={[4, 4, 0, 0]} />
            <Bar dataKey="delayed" name="Delayed" fill="#F87171" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}